import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Sparkles, Shield, Database, Users, AlertTriangle, Lightbulb, Globe2,
  Eye, MessageSquare, Lock, Send, Loader2, CheckCircle2, AlertCircle, ListChecks, Plus,
  ScrollText, ChevronDown, ChevronRight, ExternalLink, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FEATURE_ICONS = {
  internal_search: Database,
  summaries: Users,
  missing_data: AlertTriangle,
  staff_actions: Lightbulb,
  external_fallback: Globe2,
  safety_review: Shield,
};

// Suggested prompt buttons — the order is intentional, top row =
// most-common Phase 1 staff actions. Phase 6 added the trailing two
// general-knowledge prompts that exercise the external fallback path.
const SUGGESTED_PROMPTS = [
  { id: 'summarize-player', label: 'Summarize a player', text: 'Summarize player ' },
  { id: 'summarize-squad', label: 'Summarize a squad', text: 'Summarize the U19 squad this week.' },
  { id: 'pdp', label: 'Who needs PDP updates?', text: 'Who needs PDP updates?' },
  { id: 'missing-data', label: 'Who has missing data?', text: 'Who has missing data?' },
  { id: 'injury', label: 'Show injury concerns', text: 'Show injury concerns.' },
  { id: 'attendance', label: 'Low attendance concerns', text: 'Show low attendance concerns.' },
  { id: 'briefing', label: 'Weekly staff briefing', text: 'Prepare a basic weekly staff briefing.' },
  { id: 'general-session', label: 'General session structure', text: 'What is a general recovery-friendly session structure?' },
  { id: 'general-pdp', label: 'General PDP examples for a winger', text: 'What are general PDP goal examples for a winger?' },
];

const SOURCE_BADGE = {
  internal: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  external: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800',
  mixed: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  not_enough_data: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};
const SOURCE_LABELS = {
  internal: 'Internal PFAP Data',
  external: 'General Sports Knowledge',
  mixed: 'Mixed: PFAP Data + General Knowledge',
  not_enough_data: 'Not Enough Data',
};
const CONF_BADGE = {
  high: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  low: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

const PRIORITY_BADGE = {
  high: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900',
  medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
};
const CATEGORIES = ['pdp', 'attendance', 'injury_recovery', 'wellness', 'documents',
  'academic', 'career', 'performance', 'admin', 'general'];
const PRIORITIES = ['low', 'medium', 'high'];

// Phase 4 — safety classification badge styles.
const SAFETY_BADGE = {
  allowed: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
  needs_review: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  blocked: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900',
};

// Phase 3 + 4 — Each suggested action is rendered as its own card with a
// "Create Focus Hub Action" affordance that opens a confirmation modal.
// Strings (Phase 1) coerce to a minimal action object. Phase 4 adds an
// assignable-staff picker, semantic safety preflight, deep-link, and
// "AI Suggestion" / "Staff-confirmed" UI labels.
const SuggestedActionCard = ({ action, index, auditLogId, canCreate, alreadyCreated, alreadyMeta, onCreated, assignableStaff }) => {
  const a = typeof action === 'string'
    ? { id: `legacy-${index}`, title: action, description: action, category: 'general', priority: 'low', requires_human_confirmation: true }
    : (action || {});

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [extraConfirmed, setExtraConfirmed] = useState(false);
  const [title, setTitle] = useState(a.title || 'Staff follow-up');
  const [description, setDescription] = useState(a.description || a.title || '');
  const [priority, setPriority] = useState(a.priority || 'low');
  const [category, setCategory] = useState(a.category || 'general');
  const [dueDate, setDueDate] = useState(a.due_date_suggestion || '');
  const [assignedTo, setAssignedTo] = useState('');
  const [safety, setSafety] = useState(null);
  const [safetyChecking, setSafetyChecking] = useState(false);
  const [error, setError] = useState(null);

  const isRegexBlocked = !!a.blocked;
  const blockedReason = a.blocked_reason;

  // Phase 4 — re-run safety preflight whenever the modal is opened or
  // the title/description changes, so the displayed classification
  // matches what the backend will enforce on Create.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSafetyChecking(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const r = await axios.post(`${API}/api/ai/actions/safety-check`, {
          title, description,
        }, { headers });
        if (!cancelled) setSafety(r.data);
      } catch (e) {
        if (!cancelled) setSafety({ classification: 'allowed', reason: '', source: 'fallback' });
      } finally {
        if (!cancelled) setSafetyChecking(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [open, title, description]);

  const safetyClass = safety?.classification || (isRegexBlocked ? 'blocked' : 'allowed');
  const isBlocked = isRegexBlocked || safetyClass === 'blocked';
  const isNeedsReview = safetyClass === 'needs_review';

  const submit = async () => {
    if (!confirmed || isBlocked) return;
    if (isNeedsReview && !extraConfirmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const r = await axios.post(`${API}/api/ai/actions/create-focus-action`, {
        audit_log_id: auditLogId,
        suggested_action_id: a.id || `idx-${index}`,
        title,
        description,
        category,
        priority,
        due_date: dueDate || null,
        assigned_to: assignedTo || null,
        related_athlete_ids: a.related_athlete_ids || [],
        related_team_ids: a.related_team_ids || [],
        human_confirmed: true,
        human_review_extra_confirmed: !!extraConfirmed,
        edited: title !== (a.title || '') || description !== (a.description || ''),
        original_title: a.title || '',
        original_description: a.description || '',
      }, { headers });
      if (r.data?.duplicate) {
        toast.info('A Focus Hub action already exists for this AI suggestion.');
      } else {
        toast.success('Focus Hub action created.');
      }
      onCreated && onCreated(r.data);
      setOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Could not create Focus Hub action.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`ai-action-${a.id || index}`}
      className="text-[12px] px-3 py-2 rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-900/10 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${alreadyCreated ? 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800/60 dark:text-emerald-100' : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'}`} data-testid={`ai-action-label-${index}`}>
              {alreadyCreated ? 'Staff-confirmed Focus Hub Action' : 'AI Suggestion'}
            </span>
            <span className="font-medium text-gray-900 dark:text-gray-100 break-words">{a.title}</span>
          </div>
          {a.description && a.description !== a.title && (
            <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug">{a.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-1 pt-0.5">
            {a.priority && (
              <Badge variant="outline" className={`text-[10px] ${PRIORITY_BADGE[a.priority] || PRIORITY_BADGE.low}`} data-testid={`ai-action-priority-${index}`}>
                {a.priority}
              </Badge>
            )}
            {a.category && (
              <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-700 dark:text-gray-300 dark:border-gray-700">
                {a.category}
              </Badge>
            )}
            {a.recommended_owner_role && (
              <span className="text-[10px] text-gray-500">→ {a.recommended_owner_role}</span>
            )}
          </div>
          {a.safety_note && (
            <p className="text-[10px] text-amber-800 dark:text-amber-300 flex items-center gap-1">
              <Shield size={10} /> {a.safety_note}
            </p>
          )}
          {isRegexBlocked && (
            <p className="text-[10px] text-rose-700 dark:text-rose-300 flex items-center gap-1" data-testid={`ai-action-blocked-${index}`}>
              <AlertTriangle size={10} /> Blocked by safety rules: {blockedReason}
            </p>
          )}
          {alreadyCreated && (
            <div className="text-[10px] text-emerald-800 dark:text-emerald-300 space-y-0.5" data-testid={`ai-action-created-meta-${index}`}>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={10} /> Human confirmed
                {alreadyMeta?.created_at ? <span className="text-gray-500"> · {new Date(alreadyMeta.created_at).toLocaleString()}</span> : null}
              </div>
              {alreadyMeta?.created_by_name && (
                <div className="text-gray-500">by {alreadyMeta.created_by_name}</div>
              )}
              {alreadyMeta?.focus_action_status && (
                <div
                  className="flex items-center gap-1"
                  data-testid={`ai-action-focus-status-${index}`}
                >
                  <span className="text-gray-500">Focus Hub status:</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      alreadyMeta.focus_action_status === 'completed'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800'
                        : alreadyMeta.focus_action_status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800'
                        : alreadyMeta.focus_action_status === 'overdue'
                        ? 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900'
                        : alreadyMeta.focus_action_status === 'open'
                        ? 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800'
                        : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                    }`}
                  >
                    {String(alreadyMeta.focus_action_status).replace('_', ' ')}
                  </Badge>
                  {alreadyMeta.focus_action_completed_at && (
                    <span className="text-gray-500">
                      · {new Date(alreadyMeta.focus_action_completed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 flex flex-col items-end gap-1">
          {alreadyCreated ? (
            <>
              <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800" data-testid={`ai-action-created-${index}`}>
                <CheckCircle2 size={10} className="mr-1" /> Created in Focus Hub
              </Badge>
              <a
                href={alreadyMeta?.focus_action_deep_link || (alreadyMeta?.focus_action_id ? `/focus-hub?item=${alreadyMeta.focus_action_id}` : alreadyMeta?.focus_action_url || '/focus-hub')}
                data-testid={`ai-action-open-fh-${index}`}
                className="text-[10px] text-emerald-700 dark:text-emerald-300 underline hover:text-emerald-800"
                title="Open this AI-created action in Focus Hub."
              >
                Open in Focus Hub →
              </a>
            </>
          ) : !canCreate ? (
            <span className="text-[10px] text-gray-400 italic" data-testid={`ai-action-noperm-${index}`}>
              No permission to create
            </span>
          ) : isRegexBlocked ? (
            <span className="text-[10px] text-rose-600 italic">Cannot create — safety rule</span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
              data-testid={`ai-action-create-btn-${index}`}
              className="h-7 text-[11px] px-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
            >
              <Plus size={11} className="mr-1" /> Create Focus Hub Action
            </Button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`ai-action-modal-${index}`} className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Focus Hub Action</DialogTitle>
            <DialogDescription className="text-[12px]">
              Review and edit before creating. PFAP AI does not create Focus Hub actions automatically — your confirmation is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Original AI suggestion shown read-only above the editable form */}
            <div className="px-2 py-1.5 rounded bg-sky-50/60 dark:bg-sky-900/10 border border-sky-200 dark:border-sky-900/40">
              <div className="text-[9px] font-medium uppercase text-sky-800 dark:text-sky-300">Original AI suggestion</div>
              <div className="text-[11px] text-gray-800 dark:text-gray-200 mt-0.5">{a.title}</div>
              {a.description && a.description !== a.title && (
                <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">{a.description}</div>
              )}
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Title (editable)</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`ai-action-modal-title-${index}`} className="text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Description (editable)</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid={`ai-action-modal-desc-${index}`} className="text-sm min-h-[80px]" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid={`ai-action-modal-cat-${index}`} className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid={`ai-action-modal-prio-${index}`} className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Due date</label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid={`ai-action-modal-due-${index}`} className="text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Assign to</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} data-testid={`ai-action-modal-assignee-${index}`} className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
                <option value="">— me (default) —</option>
                {(assignableStaff || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.full_name} {s.role ? `· ${s.role}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-gray-500 mt-0.5">Only Admin/Staff users are listed. Players and parents cannot be assignees.</p>
            </div>
            {/* Phase 4 — safety classification badge + reason */}
            {safety && (
              <div data-testid={`ai-action-modal-safety-${index}`} className={`px-2 py-1.5 rounded text-[11px] flex items-start gap-1.5 ${
                safetyClass === 'allowed'
                  ? 'bg-emerald-50/60 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200'
                  : safetyClass === 'needs_review'
                  ? 'bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200'
                  : 'bg-rose-50/60 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-900 text-rose-900 dark:text-rose-200'
              }`}>
                <Shield size={12} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">
                    Safety: <Badge variant="outline" className={`text-[10px] ${SAFETY_BADGE[safetyClass] || SAFETY_BADGE.allowed}`}>{safetyClass.replace('_', ' ')}</Badge>
                    {safety.source && <span className="text-[10px] text-gray-500 ml-1">· {safety.source}{safety.source === 'cache' ? ' (cached verdict)' : ''}</span>}
                    {safetyChecking && <span className="text-[10px] text-gray-400 ml-1">re-checking…</span>}
                  </div>
                  <div className="text-[11px] mt-0.5">{safety.reason}</div>
                </div>
              </div>
            )}
            {a.safety_note && (
              <p className="text-[11px] text-amber-800 dark:text-amber-300 flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50/60 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <Shield size={12} /> {a.safety_note}
              </p>
            )}
            <p className="text-[11px] text-gray-500 italic">
              Source: PFAP AI Assistant · audit log {auditLogId ? auditLogId.slice(0, 8) : '—'}…
            </p>
            <label className="flex items-start gap-2 text-[12px] text-gray-800 dark:text-gray-200 cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid={`ai-action-modal-confirm-${index}`} />
              <span>I confirm this is a staff-reviewed action and not an automatic AI decision.</span>
            </label>
            {isNeedsReview && (
              <label className="flex items-start gap-2 text-[12px] text-amber-900 dark:text-amber-200 cursor-pointer px-2 py-1 rounded bg-amber-50/60 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <Checkbox checked={extraConfirmed} onCheckedChange={(v) => setExtraConfirmed(!!v)} data-testid={`ai-action-modal-extra-${index}`} />
                <span>I understand this action requires professional human review before execution.</span>
              </label>
            )}
            {error && (
              <p className="text-[11px] text-rose-700 dark:text-rose-300 flex items-center gap-1" data-testid={`ai-action-modal-error-${index}`}>
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!confirmed || submitting || !title.trim() || !description.trim() || isBlocked || (isNeedsReview && !extraConfirmed)}
              data-testid={`ai-action-modal-submit-${index}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating…</> : 'Create Focus Hub Action'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ResponseCard = ({ resp, canCreateActions, createdMap, setCreatedMap, assignableStaff }) => {
  if (!resp) return null;
  return (
    <Card data-testid="ai-response-card" className="border-emerald-200 dark:border-emerald-900/40">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
          <MessageSquare size={16} className="text-emerald-600" />
          PFAP AI Response
          {resp.intent && (
            <span className="text-[10px] font-normal text-gray-500" data-testid="ai-response-intent">
              · {resp.intent.replace(/_/g, ' ')}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid="ai-response-answer">
          {resp.answer}
        </p>

        {/* Phase 7 — query-level safety surfacing */}
        {resp.query_safety_classification === 'blocked' && (
          <div
            data-testid="ai-query-blocked-notice"
            className="px-3 py-2.5 rounded-md border border-rose-200 dark:border-rose-900 bg-rose-50/80 dark:bg-rose-950/30 text-rose-900 dark:text-rose-200 text-[11px] flex items-start gap-2"
          >
            <Lock size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-medium">
                PFAP AI cannot help with this request because it may require a qualified professional decision. I can help summarise available PFAP Hub data or identify missing information.
              </div>
              {resp.query_safety_reason && (
                <div className="text-[10px] text-rose-800/80 dark:text-rose-300/80">
                  Reason: {resp.query_safety_reason}
                </div>
              )}
              {resp.query_safety_source && (
                <div className="text-[10px] text-rose-800/60 dark:text-rose-300/60">
                  Detected by: {resp.query_safety_source}
                </div>
              )}
            </div>
          </div>
        )}
        {resp.query_safety_classification === 'needs_review' && (
          <div
            data-testid="ai-query-needs-review-notice"
            className="px-3 py-2.5 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/80 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-[11px] flex items-start gap-2"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-medium">
                This request requires professional human review. PFAP AI can provide a support summary only and cannot make final decisions.
              </div>
              {resp.query_safety_reason && (
                <div className="text-[10px] text-amber-800/80 dark:text-amber-300/80">
                  Reason: {resp.query_safety_reason}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={`text-[10px] ${SOURCE_BADGE[resp.source_type] || SOURCE_BADGE.not_enough_data}`} data-testid="ai-source-badge">
            {resp.source_label || SOURCE_LABELS[resp.source_type] || 'Not Enough Data'}
          </Badge>
          <Badge variant="outline" className={`text-[10px] ${CONF_BADGE[resp.confidence_level] || CONF_BADGE.low}`} data-testid="ai-confidence-badge">
            confidence: {resp.confidence_level}
          </Badge>
          {resp.requires_human_review && (
            <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900" data-testid="ai-review-badge">
              human review required
            </Badge>
          )}
          {resp.external_fallback_used && (
            <Badge
              variant="outline"
              className="text-[10px] bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900"
              data-testid="ai-external-used-badge"
            >
              <Globe2 size={10} className="inline mr-1" /> External knowledge: Yes
            </Badge>
          )}
          {resp.query_safety_classification === 'allowed' && (
            <Badge
              variant="outline"
              className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
              data-testid="ai-query-safety-checked"
              title={resp.query_safety_reason || 'Query passed PFAP AI safety checks.'}
            >
              <ShieldCheck size={10} className="inline mr-1" /> Safety checked
            </Badge>
          )}
        </div>

        {/* Phase 6 — external fallback disclaimer + metadata */}
        {(resp.source_type === 'external' || resp.source_type === 'mixed') && (
          <div
            data-testid="ai-external-disclaimer"
            className="px-3 py-2 rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50/70 dark:bg-sky-900/15 text-sky-900 dark:text-sky-200 text-[11px] flex items-start gap-2"
          >
            <Globe2 size={12} className="mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-medium">
                {resp.source_type === 'external'
                  ? 'This answer uses general sports knowledge because PFAP Hub does not yet have enough internal data for this request. It should be reviewed by qualified PFAP staff before use.'
                  : 'This answer combines PFAP Hub data with general sports knowledge. PFAP staff should review before applying it.'}
              </div>
              {resp.external_knowledge_category_label && (
                <div className="text-[10px] text-sky-800/80 dark:text-sky-300/80">
                  Category: {resp.external_knowledge_category_label}
                </div>
              )}
              {resp.fallback_reason && (
                <div className="text-[10px] text-sky-800/70 dark:text-sky-300/70">
                  Reason: {resp.fallback_reason.replace(/_/g, ' ')}
                </div>
              )}
              <div className="text-[10px] text-sky-800/60 dark:text-sky-300/60 italic">
                No web search was performed. Source: general LLM sports knowledge — no fabricated citations.
              </div>
            </div>
          </div>
        )}

        {/* Phase 6 — fallback blocked notice */}
        {resp.fallback_blocked && (
          <div
            data-testid="ai-fallback-blocked"
            className="px-3 py-2 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-900/15 text-amber-900 dark:text-amber-200 text-[11px] flex items-start gap-2"
          >
            <Lock size={12} className="mt-0.5 shrink-0" />
            <div>
              External fallback blocked for safety.
              {resp.fallback_block_reason && (
                <span className="text-[10px] text-amber-800/80 dark:text-amber-300/80 block">
                  {resp.fallback_block_reason}
                </span>
              )}
            </div>
          </div>
        )}

        {Array.isArray(resp.internal_modules_used) && resp.internal_modules_used.length > 0 && (
          <div data-testid="ai-modules-used">
            <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              <Database size={12} /> Internal modules used
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-normal ml-1">· Data available</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {resp.internal_modules_used.map((m) => (
                <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{m}</span>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(resp.permission_limited_modules) && resp.permission_limited_modules.length > 0 && (
          <div data-testid="ai-permission-limited">
            <div className="text-[11px] font-medium text-amber-800 dark:text-amber-300 mb-1 flex items-center gap-1">
              <Lock size={12} /> Permission-limited modules
              <span className="text-[10px] font-normal ml-1">· not visible to your role</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {resp.permission_limited_modules.map((m) => (
                <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">{m}</span>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(resp.data_modules_skipped) && resp.data_modules_skipped.length > 0 && (
          <div data-testid="ai-modules-skipped">
            <div className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
              <AlertCircle size={12} /> No data yet
            </div>
            <div className="flex flex-wrap gap-1">
              {resp.data_modules_skipped.map((m) => (
                <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">{m}</span>
              ))}
            </div>
          </div>
        )}

        {resp.ambiguity && Array.isArray(resp.ambiguity.matches) && resp.ambiguity.matches.length > 0 && (
          <div data-testid="ai-ambiguity-block" className="border border-amber-200 dark:border-amber-900/40 rounded-md bg-amber-50/60 dark:bg-amber-900/10 p-3">
            <div className="text-[12px] font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5 mb-1">
              <AlertTriangle size={14} /> Needs clarification
            </div>
            <p className="text-[12px] text-amber-900/90 dark:text-amber-200/90 mb-2">{resp.ambiguity.message}</p>
            <ul className="space-y-1">
              {resp.ambiguity.matches.map((m, i) => (
                <li key={m.id || i} data-testid={`ai-ambiguity-match-${i}`} className="text-[12px] text-gray-800 dark:text-gray-200 flex items-center justify-between gap-2 px-2 py-1 rounded bg-white dark:bg-gray-900 border border-amber-100 dark:border-amber-900/30">
                  <span>
                    <strong>{m.full_name || m.squad}</strong>
                    {m.squad ? <span className="text-gray-500 ml-1">· {m.squad}</span> : null}
                    {m.position ? <span className="text-gray-500 ml-1">· {m.position}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(resp.missing_data) && resp.missing_data.length > 0 && (
          <div data-testid="ai-missing-data">
            <div className="text-[11px] font-medium text-amber-800 dark:text-amber-300 mb-1 flex items-center gap-1">
              <AlertTriangle size={12} /> Missing data
            </div>
            <ul className="list-disc list-inside text-[12px] text-gray-700 dark:text-gray-300 space-y-0.5">
              {resp.missing_data.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {Array.isArray(resp.suggested_actions) && resp.suggested_actions.length > 0 && (
          <div data-testid="ai-suggested-actions">
            <div className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300 mb-1 flex items-center gap-1">
              <ListChecks size={12} /> Suggested staff actions
              <span className="text-[10px] font-normal text-gray-500 ml-1">· human review required before any Focus Hub action is created</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {resp.suggested_actions.map((a, i) => {
                const sid = (a && a.id) || `idx-${i}`;
                return (
                  <SuggestedActionCard
                    key={sid}
                    action={a}
                    index={i}
                    auditLogId={resp.audit_log_id}
                    canCreate={canCreateActions}
                    alreadyCreated={!!createdMap[sid]}
                    alreadyMeta={createdMap[sid] || null}
                    assignableStaff={assignableStaff}
                    onCreated={(meta) => setCreatedMap((m) => ({ ...m, [sid]: meta || true }))}
                  />
                );
              })}
            </div>
          </div>
        )}

        {Array.isArray(resp.safety_notes) && resp.safety_notes.length > 0 && (
          <div data-testid="ai-safety-notes" className="pt-1 border-t border-gray-100 dark:border-gray-800">
            <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              <Shield size={12} /> Safety notes
            </div>
            <ul className="list-disc list-inside text-[12px] text-gray-700 dark:text-gray-300 space-y-0.5">
              {resp.safety_notes.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}

        {resp.audit_log_id && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500" data-testid="ai-audit-ref">
            audit log · {resp.audit_log_id.slice(0, 8)}…
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const AuditLogViewer = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const r = await axios.get(`${API}/api/ai/audit-logs?limit=50`, { headers });
      setRows(r.data?.items || []);
      setLoaded(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) fetchLogs();
  };

  return (
    <Card data-testid="ai-audit-log-viewer">
      <CardHeader className="pb-2 cursor-pointer" onClick={toggle}>
        <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
          <ScrollText size={16} className="text-emerald-600" />
          AI Audit Log Viewer
          <span className="text-[10px] text-gray-400 font-normal ml-1">(read-only · ai_assistant_audit_logs)</span>
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500">
            {loaded && !loading && <span data-testid="ai-audit-log-count">{rows.length} rows</span>}
            {loading && <Loader2 size={14} className="animate-spin" />}
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 mb-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchLogs}
              disabled={loading}
              data-testid="ai-audit-log-refresh"
              className="h-7 text-[11px] px-2"
            >
              <Loader2 className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : 'hidden'}`} />
              Refresh
            </Button>
            <p className="text-[11px] text-gray-500">Most recent 50 PFAP AI queries.</p>
          </div>
          {error && (
            <p
              className="text-[12px] text-rose-700 dark:text-rose-300 flex items-center gap-1.5"
              data-testid="ai-audit-log-error"
            >
              <AlertCircle size={12} /> {error}
            </p>
          )}
          {!error && loaded && rows.length === 0 && (
            <p className="text-[12px] text-gray-500" data-testid="ai-audit-log-empty">
              No audit log entries yet. Run a PFAP AI query to populate this list.
            </p>
          )}
          {rows.length > 0 && (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="ai-audit-log-rows">
              {rows.map((row, i) => {
                const status = row.status || 'success';
                const statusColor =
                  status === 'success'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                    : status === 'blocked'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200 dark:border-rose-900';
                return (
                  <div
                    key={row.id || i}
                    className="px-2.5 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40 text-[11px] space-y-0.5"
                    data-testid={`ai-audit-log-row-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${statusColor}`}>{status}</Badge>
                      {row.response_type && (
                        <span className="text-gray-600 dark:text-gray-400">
                          {row.response_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      <span className="text-gray-400 ml-auto">
                        {row.timestamp ? new Date(row.timestamp).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="text-gray-800 dark:text-gray-200 line-clamp-2">
                      {row.query_text || '—'}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 flex-wrap">
                      {row.user_name && <span>{row.user_name}</span>}
                      {row.user_role && <span>· {row.user_role}</span>}
                      {row.confidence_level && <span>· conf: {row.confidence_level}</span>}
                      {row.source_type && <span>· src: {row.source_type}</span>}
                      {Array.isArray(row.modules_accessed) && row.modules_accessed.length > 0 && (
                        <span title={row.modules_accessed.join(', ')}>
                          · modules: {row.modules_accessed.length}
                        </span>
                      )}
                      {row.action_suggestions_created > 0 && (
                        <span>· actions: {row.action_suggestions_created}</span>
                      )}
                      {row.id && (
                        <span className="font-mono text-gray-400">
                          · {String(row.id).slice(0, 8)}…
                        </span>
                      )}
                    </div>
                    {row.error_message && (
                      <div className="text-[10px] text-rose-600 dark:text-rose-400 flex items-center gap-1">
                        <AlertCircle size={10} /> {row.error_message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const PFAPAIAssistant = () => {
  const [status, setStatus] = useState(null);
  const [rules, setRules] = useState([]);
  const [perms, setPerms] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState(null);

  // Phase 1 — query state
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState(null);
  const [queryError, setQueryError] = useState(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    Promise.all([
      axios.get(`${API}/api/ai/status`, { headers }),
      axios.get(`${API}/api/ai/safety-rules`, { headers }),
      axios.get(`${API}/api/ai/permissions`, { headers }),
    ])
      .then(([s, r, p]) => {
        setStatus(s.data);
        setRules(r.data.rules || []);
        setPerms(p.data);
      })
      .catch((e) => setMetaError(e.response?.data?.detail || 'Could not load AI Assistant foundation'))
      .finally(() => setLoadingMeta(false));
  }, []);

  const canQuery = perms?.permissions?.ai_assistant_query && perms.permissions.ai_assistant_query !== 'none';
  const canCreateActions = perms?.permissions?.ai_assistant_staff_actions && perms.permissions.ai_assistant_staff_actions !== 'none';
  const canViewAuditLogs = perms?.permissions?.ai_assistant_audit_logs && perms.permissions.ai_assistant_audit_logs !== 'none';
  const providerConfigured = !!status?.provider?.configured;

  // Map of suggested_action_id → meta dict once promoted, used to
  // disable duplicate creation and show "Created in Focus Hub".
  const [createdMap, setCreatedMap] = useState({});
  // Phase 4 — assignable staff list for the modal picker.
  const [assignableStaff, setAssignableStaff] = useState([]);

  useEffect(() => {
    if (!canCreateActions) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    axios.get(`${API}/api/ai/actions/assignable-staff?limit=200`, { headers })
      .then((r) => setAssignableStaff(r.data?.items || []))
      .catch(() => setAssignableStaff([]));
  }, [canCreateActions]);

  const submit = async (text) => {
    const q = (text ?? query).trim();
    if (!q || submitting || !canQuery) return;
    setSubmitting(true);
    setQueryError(null);
    setResponse(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const r = await axios.post(`${API}/api/ai/query`, { query: q }, { headers });
      setResponse(r.data);
      setCreatedMap({}); // fresh response → reset created tracking
      // Phase 4 — duplicate awareness: fetch any prior creations for
      // this audit_log_id so existing items show "Created in Focus Hub"
      // immediately on render.
      if (r.data?.audit_log_id && canCreateActions) {
        try {
          const created = await axios.get(
            `${API}/api/ai/actions/created?audit_log_id=${r.data.audit_log_id}&limit=200`,
            { headers },
          );
          const map = {};
          for (const row of created.data?.items || []) {
            map[row.suggested_action_id] = {
              focus_action_id: row.focus_action_id,
              focus_action_url: row.focus_action_url,
              focus_action_deep_link: row.focus_action_deep_link,
              focus_action_status: row.focus_action_status,
              focus_action_completed_at: row.focus_action_completed_at,
              created_at: row.created_at,
              created_by_name: row.created_by_user_name,
            };
          }
          setCreatedMap(map);
        } catch {
          // best-effort — non-critical
        }
      }
    } catch (e) {
      setQueryError(e.response?.data?.detail || e.message || 'AI query failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onPickPrompt = (text) => {
    setQuery(text);
    if (!text.endsWith(' ')) submit(text);
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="pfap-ai-assistant-page">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Sparkles className="text-emerald-600" size={28} />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              PFAP AI Assistant
            </h1>
            <Badge
              data-testid="ai-mode-badge"
              className={
                providerConfigured
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
              }
            >
              {providerConfigured ? 'Phase 1 — Live' : 'Foundation Mode'}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Academy intelligence assistant for search, summaries, staff actions, and future decision support.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 italic" data-testid="ai-guardrail-note">
            PFAP AI supports staff decision-making. It does not replace coaches, physios, nutritionists, or academy leadership.
          </p>
        </div>

        {/* Foundation banner */}
        <Card className={providerConfigured
            ? 'border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/10 dark:border-emerald-800'
            : 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800'
        }>
          <CardContent className="p-4 flex items-start gap-3">
            <Eye className={(providerConfigured ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300') + ' shrink-0 mt-0.5'} size={18} />
            <div className="space-y-1">
              <p className={(providerConfigured ? 'text-emerald-900 dark:text-emerald-200' : 'text-amber-900 dark:text-amber-200') + ' text-sm font-medium'}>
                {status?.message || 'PFAP AI Assistant is being prepared. Full AI search and recommendations will be added in future phases.'}
              </p>
              <p className={(providerConfigured ? 'text-emerald-800/80 dark:text-emerald-300/80' : 'text-amber-800/80 dark:text-amber-300/80') + ' text-xs'}>
                PFAP AI searches PFAP Hub data first before using any general sports knowledge. <strong>It does not replace coaches, physios, nutritionists, or academy leadership.</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Phase 1 — query box */}
        <Card data-testid="ai-query-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
              <MessageSquare size={16} className="text-emerald-600" />
              Ask PFAP AI
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!canQuery && (
              <p className="text-[12px] text-gray-500" data-testid="ai-query-locked">
                You can view PFAP AI but not run queries. Ask an admin for the
                <code className="mx-1 px-1 bg-gray-100 dark:bg-gray-800 rounded">ai_assistant_query</code>
                permission.
              </p>
            )}

            <div className="flex flex-wrap gap-2" data-testid="ai-suggested-prompts">
              {SUGGESTED_PROMPTS.map((p) => (
                <Button
                  key={p.id}
                  size="sm"
                  variant="outline"
                  disabled={!canQuery || submitting}
                  onClick={() => onPickPrompt(p.text)}
                  data-testid={`ai-prompt-${p.id}`}
                  className="text-[11px] border-emerald-200 text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-900 dark:hover:bg-emerald-900/20"
                >
                  {p.label}
                </Button>
              ))}
            </div>

            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
              placeholder={canQuery
                ? "Ask about a player, squad, missing data, PDP follow-ups, or a weekly briefing… (Cmd/Ctrl + Enter to submit)"
                : 'AI Query Coming Soon — request the ai_assistant_query permission to enable.'
              }
              disabled={!canQuery || submitting}
              data-testid="ai-query-input"
              className="min-h-[80px] text-sm"
            />
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-gray-500">
                {providerConfigured
                  ? 'Anthropic Claude Sonnet 4.5 via Emergent · grounded on internal PFAP Hub data'
                  : 'No AI provider configured — falls back to rule-based summaries.'}
              </p>
              <Button
                size="sm"
                onClick={() => submit()}
                disabled={!canQuery || submitting || !query.trim()}
                data-testid="ai-query-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Asking PFAP AI…</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1.5" /> Submit</>
                )}
              </Button>
            </div>

            {queryError && (
              <p className="text-[12px] text-rose-700 dark:text-rose-300 flex items-center gap-1.5" data-testid="ai-query-error">
                <AlertCircle size={14} /> {queryError}
              </p>
            )}

            {!response && !submitting && !queryError && (
              <div className="text-[12px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5" data-testid="ai-query-empty-state">
                <CheckCircle2 size={14} /> Ready when you are. Pick a prompt above or type your own question.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response display */}
        {response && (
          <ResponseCard
            resp={response}
            canCreateActions={canCreateActions}
            createdMap={createdMap}
            setCreatedMap={setCreatedMap}
            assignableStaff={assignableStaff}
          />
        )}

        {/* Feature roadmap */}
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Feature Roadmap</h2>
          {loadingMeta && <p className="text-xs text-gray-500">Loading…</p>}
          {metaError && <p className="text-xs text-red-600" data-testid="ai-error">{metaError}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ai-feature-grid">
            {(status?.available_features || []).map((f) => {
              const Icon = FEATURE_ICONS[f.key] || Sparkles;
              const isReady = f.status === 'Foundation ready';
              return (
                <Card key={f.key} data-testid={`ai-feature-${f.key}`} className="hover:shadow-sm transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
                      <Icon size={16} className="text-emerald-600" />
                      {f.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    <p className="text-[12px] leading-relaxed text-gray-600 dark:text-gray-400">{f.summary}</p>
                    <Badge
                      variant="outline"
                      className={
                        isReady
                          ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300 dark:border-emerald-800 text-[10px]'
                          : 'border-gray-300 text-gray-600 dark:text-gray-400 text-[10px]'
                      }
                    >
                      {f.status}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Safety rules */}
        <Card data-testid="ai-safety-rules">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
              <Shield size={16} className="text-emerald-600" />
              Safety Rules ({rules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-1 text-[12px] text-gray-700 dark:text-gray-300 leading-relaxed">
              {rules.map((r, i) => (
                <li key={i} data-testid={`ai-rule-${i}`}>{r}</li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* Phase 5 — AI Audit Log Viewer (gated) */}
        {canViewAuditLogs && <AuditLogViewer />}

        {/* Current user AI permissions */}
        {perms && (
          <Card data-testid="ai-user-permissions">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
                <Lock size={16} className="text-emerald-600" />
                Your AI Permissions ({perms.role || 'unknown role'})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
                {Object.entries(perms.permissions || {}).map(([key, level]) => {
                  const allowed = level !== 'none';
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between py-0.5 border-b border-gray-100 dark:border-gray-800"
                      data-testid={`ai-perm-${key}`}
                    >
                      <span className="text-gray-600 dark:text-gray-400 truncate">{key.replace('ai_assistant_', '')}</span>
                      <span className={allowed ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-gray-400 dark:text-gray-500'}>
                        {level}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PFAPAIAssistant;
